import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { authApi, myEmployeeDetailsApi } from '../services/api';
import type { EmployeeMfaStatus, MfaMethod } from '../types';
import { formatCompanyDateTime } from '../utils/companyDateFormat';

const MFA_METHOD_LABELS: Record<MfaMethod, string> = {
  Authenticator: 'Authenticator app',
  Email: 'Email',
  Sms: 'SMS',
};

const MyAccount: React.FC = () => {
  const { employee, updateEmployee } = useAuth();
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordQuestion, setPasswordQuestion] = useState('');
  const [passwordAnswer, setPasswordAnswer] = useState('');

  const [personalDetailsLoading, setPersonalDetailsLoading] = useState(false);
  const [personalDetailsSaving, setPersonalDetailsSaving] = useState(false);
  const [personalDetails, setPersonalDetails] = useState({
    phone: '',
    gender: '',
    genderOther: '',
    birthday: '',

    address1: '',
    address2: '',
    address3: '',
    county: '',
    zipCode: '',

    nextOfKinName: '',
    nextOfKinPhone: '',
    nextOfKinAddress: '',

    allergies: '',
    medicalNotes: '',
  });

  const [mfaStatus, setMfaStatus] = useState<EmployeeMfaStatus | null>(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [mfaMethod, setMfaMethod] = useState<MfaMethod>('Authenticator');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaCodeSent, setMfaCodeSent] = useState(false);
  const [mfaDestination, setMfaDestination] = useState<string | null>(null);
  const [authenticatorSecret, setAuthenticatorSecret] = useState<string | null>(null);
  const [authenticatorOtpauthUrl, setAuthenticatorOtpauthUrl] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState(false);

  const loadMfaStatus = async () => {
    setMfaLoading(true);
    setMfaError('');
    try {
      const status = await authApi.mfa.getMe();
      setMfaStatus(status);
      const preferred =
        status.defaultMethod && status.allowedMethods.includes(status.defaultMethod)
          ? status.defaultMethod
          : status.allowedMethods[0] || 'Authenticator';
      setMfaMethod(preferred);
    } catch (err: any) {
      setMfaError(err?.response?.data?.message || err?.message || 'Failed to load MFA status.');
      setMfaStatus(null);
    } finally {
      setMfaLoading(false);
    }
  };

  useEffect(() => {
    if (employee) {
      setEmail(employee.email || '');
      setPasswordQuestion(employee.passwordQuestion || '');
      setPasswordAnswer(employee.passwordAnswer || '');
      loadMfaStatus();

      setPersonalDetailsLoading(true);
      myEmployeeDetailsApi.get()
        .then((details) => {
          setPersonalDetails({
            phone: (details.phone ?? '') as string,
            gender: (details.gender ?? '') as string,
            genderOther: (details.genderOther ?? '') as string,
            birthday: (details.birthday ?? '') as string,

            address1: (details.address1 ?? '') as string,
            address2: (details.address2 ?? '') as string,
            address3: (details.address3 ?? '') as string,
            county: (details.county ?? '') as string,
            zipCode: (details.zipCode ?? '') as string,

            nextOfKinName: (details.nextOfKinName ?? '') as string,
            nextOfKinPhone: (details.nextOfKinPhone ?? '') as string,
            nextOfKinAddress: (details.nextOfKinAddress ?? '') as string,

            allergies: (details.allergies ?? '') as string,
            medicalNotes: (details.medicalNotes ?? '') as string,
          });
        })
        .catch((err) => {
          console.error('Failed to load personal details:', err);
        })
        .finally(() => setPersonalDetailsLoading(false));
    }
  }, [employee]);

  const handleUpdateAccount = async () => {
    if (!email.trim()) {
      alert('Email is required');
      return;
    }

    if (newPassword && newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (newPassword && newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      const result = await authApi.updateAccount({
        email: email.trim(),
        newPassword: newPassword || undefined,
        passwordQuestion: passwordQuestion.trim() || undefined,
        passwordAnswer: passwordAnswer.trim() || undefined,
      });

      if (employee && result.employee) {
        updateEmployee({
          ...employee,
          email: result.employee.email,
          passwordQuestion: result.employee.passwordQuestion,
          passwordAnswer: result.employee.passwordAnswer,
        });
      }

      alert('Account updated successfully!');
      setShowUpdateModal(false);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordAnswer('');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update account';
      alert('Error: ' + errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePersonalDetails = async () => {
    if (!employee) return;
    setPersonalDetailsSaving(true);
    try {
      const legacyAddress = [
        personalDetails.address1,
        personalDetails.address2,
        personalDetails.address3,
        personalDetails.county,
        personalDetails.zipCode,
      ]
        .map((v) => v.trim())
        .filter(Boolean)
        .join(', ');

      const toNullable = (v: string) => {
        const t = v.trim();
        return t ? t : null;
      };

      const updated = await myEmployeeDetailsApi.update({
        phone: toNullable(personalDetails.phone),
        gender: toNullable(personalDetails.gender),
        genderOther: toNullable(personalDetails.genderOther),
        birthday: toNullable(personalDetails.birthday),

        address: toNullable(legacyAddress),
        address1: toNullable(personalDetails.address1),
        address2: toNullable(personalDetails.address2),
        address3: toNullable(personalDetails.address3),
        county: toNullable(personalDetails.county),
        zipCode: toNullable(personalDetails.zipCode),

        nextOfKinName: toNullable(personalDetails.nextOfKinName),
        nextOfKinPhone: toNullable(personalDetails.nextOfKinPhone),
        nextOfKinAddress: toNullable(personalDetails.nextOfKinAddress),

        allergies: toNullable(personalDetails.allergies),
        medicalNotes: toNullable(personalDetails.medicalNotes),
      });

      updateEmployee({
        ...employee,
        ...updated,
      });

      // Ensure our local form matches the server source-of-truth.
      setPersonalDetails((prev) => ({
        ...prev,
        phone: (updated.phone ?? '') as string,
        gender: (updated.gender ?? '') as string,
        genderOther: (updated.genderOther ?? '') as string,
        birthday: (updated.birthday ?? '') as string,
        address1: (updated.address1 ?? '') as string,
        address2: (updated.address2 ?? '') as string,
        address3: (updated.address3 ?? '') as string,
        county: (updated.county ?? '') as string,
        zipCode: (updated.zipCode ?? '') as string,
        nextOfKinName: (updated.nextOfKinName ?? '') as string,
        nextOfKinPhone: (updated.nextOfKinPhone ?? '') as string,
        nextOfKinAddress: (updated.nextOfKinAddress ?? '') as string,
        allergies: (updated.allergies ?? '') as string,
        medicalNotes: (updated.medicalNotes ?? '') as string,
      }));

      alert('Personal details updated successfully!');
    } catch (error: any) {
      alert(error?.response?.data?.message || error?.message || 'Failed to update personal details.');
    } finally {
      setPersonalDetailsSaving(false);
    }
  };

  const resetEnrollState = () => {
    setMfaCode('');
    setMfaCodeSent(false);
    setMfaDestination(null);
    setAuthenticatorSecret(null);
    setAuthenticatorOtpauthUrl(null);
    setEnrolling(false);
    setMfaError('');
  };

  const startEnroll = async () => {
    setMfaError('');
    setEnrolling(true);
    setMfaCode('');
    setMfaCodeSent(false);
    setAuthenticatorSecret(null);
    setAuthenticatorOtpauthUrl(null);
    if (mfaMethod === 'Authenticator') {
      try {
        const start = await authApi.mfa.startMyAuthenticator();
        setAuthenticatorSecret(start.secret);
        setAuthenticatorOtpauthUrl(start.otpauthUrl);
      } catch (err: any) {
        setMfaError(err?.response?.data?.message || err?.message || 'Failed to start authenticator setup.');
        setEnrolling(false);
      }
    }
  };

  const handleSendMyCode = async () => {
    setMfaError('');
    try {
      const result = await authApi.mfa.sendMyCode(mfaMethod);
      setMfaCodeSent(true);
      setMfaDestination(result.destination ?? null);
    } catch (err: any) {
      setMfaError(err?.response?.data?.message || err?.message || 'Failed to send code.');
    }
  };

  const handleEnableMfa = async () => {
    if (!mfaCode.trim()) {
      setMfaError('Please enter the verification code.');
      return;
    }
    setMfaError('');
    setIsLoading(true);
    try {
      const status = await authApi.mfa.enableMe(mfaMethod, mfaCode.trim());
      setMfaStatus(status);
      resetEnrollState();
      alert('MFA enabled successfully.');
    } catch (err: any) {
      setMfaError(err?.response?.data?.message || err?.message || 'Failed to enable MFA.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!window.confirm('Disable multi-factor authentication for your account?')) return;
    setMfaError('');
    setIsLoading(true);
    try {
      const status = await authApi.mfa.disableMe();
      setMfaStatus(status);
      resetEnrollState();
      alert('MFA disabled.');
    } catch (err: any) {
      setMfaError(err?.response?.data?.message || err?.message || 'Failed to disable MFA.');
    } finally {
      setIsLoading(false);
    }
  };

  const maskPassword = () => {
    return '•'.repeat(8);
  };

  if (!employee) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-600">Loading account information...</p>
      </div>
    );
  }

  const needsSendCode = mfaMethod === 'Email' || mfaMethod === 'Sms';

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">My Account</h1>

      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center py-3 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-600">Password</span>
            <span className="text-sm text-gray-900">{maskPassword()}</span>
          </div>

          <div className="flex justify-between items-center py-3 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-600">Email Address</span>
            <span className="text-sm text-gray-900">{employee.email || 'Not set'}</span>
          </div>

          <div className="py-3 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-600 block mb-2">Password Question</span>
            <span className="text-sm text-gray-900">{employee.passwordQuestion || 'Not set'}</span>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Details</h2>

        {personalDetailsLoading ? (
          <p className="text-sm text-gray-600">Loading your details…</p>
        ) : (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              await handleUpdatePersonalDetails();
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="text"
                  value={personalDetails.phone}
                  onChange={(e) => setPersonalDetails({ ...personalDetails, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="e.g. +353..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Birthday</label>
                <input
                  type="date"
                  value={personalDetails.birthday}
                  onChange={(e) => setPersonalDetails({ ...personalDetails, birthday: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <input
                  type="text"
                  value={personalDetails.gender}
                  onChange={(e) => setPersonalDetails({ ...personalDetails, gender: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Male/Female/Other"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender (Other)</label>
                <input
                  type="text"
                  value={personalDetails.genderOther}
                  onChange={(e) => setPersonalDetails({ ...personalDetails, genderOther: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Specify"
                />
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Address</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  value={personalDetails.address1}
                  onChange={(e) => setPersonalDetails({ ...personalDetails, address1: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Address line 1"
                />
                <input
                  type="text"
                  value={personalDetails.address2}
                  onChange={(e) => setPersonalDetails({ ...personalDetails, address2: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Address line 2"
                />
                <input
                  type="text"
                  value={personalDetails.address3}
                  onChange={(e) => setPersonalDetails({ ...personalDetails, address3: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Address line 3"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={personalDetails.county}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, county: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="County"
                  />
                  <input
                    type="text"
                    value={personalDetails.zipCode}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, zipCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="ZIP / Eircode"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Next of Kin</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  value={personalDetails.nextOfKinName}
                  onChange={(e) => setPersonalDetails({ ...personalDetails, nextOfKinName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Full name"
                />
                <input
                  type="text"
                  value={personalDetails.nextOfKinPhone}
                  onChange={(e) => setPersonalDetails({ ...personalDetails, nextOfKinPhone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Phone number"
                />
                <input
                  type="text"
                  value={personalDetails.nextOfKinAddress}
                  onChange={(e) => setPersonalDetails({ ...personalDetails, nextOfKinAddress: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Address"
                />
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Health / Allergies</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Allergies</label>
                  <textarea
                    value={personalDetails.allergies}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, allergies: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="List allergies (if any)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Medical Notes</label>
                  <textarea
                    value={personalDetails.medicalNotes}
                    onChange={(e) => setPersonalDetails({ ...personalDetails, medicalNotes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Any relevant medical information"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="submit"
                disabled={personalDetailsSaving || personalDetailsLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {personalDetailsSaving ? 'Saving...' : 'Save details'}
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Multi-Factor Authentication</h2>
        {mfaLoading ? (
          <p className="text-sm text-gray-600">Loading MFA status…</p>
        ) : !mfaStatus?.tenantMfaEnabled ? (
          <p className="text-sm text-gray-600">MFA is not enabled for your company.</p>
        ) : (
          <div className="space-y-3">
            {mfaError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-800">{mfaError}</p>
              </div>
            )}
            <div className="text-sm text-gray-700 space-y-1">
              <p>
                <span className="font-medium text-gray-600">Requirement:</span> {mfaStatus.requirement}
              </p>
              <p>
                <span className="font-medium text-gray-600">Status:</span>{' '}
                {mfaStatus.mfaEnabled || mfaStatus.enrolled
                  ? `Enabled${mfaStatus.method ? ` (${MFA_METHOD_LABELS[mfaStatus.method] || mfaStatus.method})` : ''}`
                  : 'Not enrolled'}
              </p>
              {mfaStatus.exemptActive && (
                <p className="text-amber-700">
                  Temporary exemption active
                  {mfaStatus.exemptUntil ? ` until ${formatCompanyDateTime(mfaStatus.exemptUntil)}` : ''}.
                </p>
              )}
            </div>

            {mfaStatus.canSelfEnable && !(mfaStatus.mfaEnabled || mfaStatus.enrolled) && !enrolling && (
              <button
                type="button"
                onClick={startEnroll}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Set up MFA
              </button>
            )}

            {enrolling && mfaStatus.canSelfEnable && (
              <div className="border border-gray-200 rounded-md p-4 space-y-3">
                {mfaStatus.allowedMethods.length > 1 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Method</p>
                    {mfaStatus.allowedMethods.map((method) => (
                      <label key={method} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="myMfaMethod"
                          checked={mfaMethod === method}
                          onChange={async () => {
                            setMfaMethod(method);
                            setMfaCode('');
                            setMfaCodeSent(false);
                            setAuthenticatorSecret(null);
                            setAuthenticatorOtpauthUrl(null);
                            if (method === 'Authenticator') {
                              try {
                                const start = await authApi.mfa.startMyAuthenticator();
                                setAuthenticatorSecret(start.secret);
                                setAuthenticatorOtpauthUrl(start.otpauthUrl);
                              } catch (err: any) {
                                setMfaError(err?.response?.data?.message || err?.message || 'Failed to start authenticator setup.');
                              }
                            }
                          }}
                          className="border-gray-300 text-blue-600"
                        />
                        <span className="text-sm text-gray-700">{MFA_METHOD_LABELS[method]}</span>
                      </label>
                    ))}
                  </div>
                )}

                {mfaMethod === 'Authenticator' && (
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-700">Scan the QR code with your authenticator app (or enter the secret), then enter the code.</p>
                    {authenticatorOtpauthUrl && (
                      <div className="flex flex-col items-center gap-2 py-2">
                        <QRCodeSVG
                          value={authenticatorOtpauthUrl}
                          size={160}
                          level="M"
                          includeMargin
                          className="rounded-md bg-white"
                        />
                        <p className="text-xs text-gray-500">Scan with your authenticator app</p>
                      </div>
                    )}
                    {authenticatorSecret && (
                      <div>
                        <span className="text-xs font-medium text-gray-600">Secret (manual entry)</span>
                        <code className="block text-xs break-all bg-gray-50 border border-gray-200 rounded px-2 py-2 mt-1">
                          {authenticatorSecret}
                        </code>
                      </div>
                    )}
                    {authenticatorOtpauthUrl && (
                      <details className="text-xs text-gray-600">
                        <summary className="cursor-pointer font-medium">Show otpauth URL</summary>
                        <code className="block text-xs break-all bg-gray-50 border border-gray-200 rounded px-2 py-2 mt-1">
                          {authenticatorOtpauthUrl}
                        </code>
                      </details>
                    )}
                  </div>
                )}

                {needsSendCode && (
                  <div>
                    <button
                      type="button"
                      onClick={handleSendMyCode}
                      disabled={isLoading}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50"
                    >
                      {mfaCodeSent ? 'Resend code' : `Send code via ${MFA_METHOD_LABELS[mfaMethod]}`}
                    </button>
                    {mfaCodeSent && (
                      <p className="text-xs text-gray-500 mt-1">
                        Code sent{mfaDestination ? ` to ${mfaDestination}` : ''}.
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Verification code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Enter code"
                    disabled={isLoading}
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={resetEnrollState}
                    disabled={isLoading}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleEnableMfa}
                    disabled={isLoading || (needsSendCode && !mfaCodeSent)}
                    className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isLoading ? 'Saving…' : 'Enable MFA'}
                  </button>
                </div>
              </div>
            )}

            {mfaStatus.canSelfDisable && (mfaStatus.mfaEnabled || mfaStatus.enrolled) && (
              <button
                type="button"
                onClick={handleDisableMfa}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-red-700 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
              >
                Disable MFA
              </button>
            )}
          </div>
        )}
      </div>

      <button
        onClick={() => setShowUpdateModal(true)}
        className="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        Update Account Information
      </button>

      {/* Update Account Modal */}
      {showUpdateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Update Account Information</h3>
              <button
                onClick={() => setShowUpdateModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="mt-4 space-y-4 max-h-96 overflow-y-auto">
              <div>
                <label htmlFor="update-email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  id="update-email"
                  type="email"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                  New Password (Optional)
                </label>
                <input
                  id="new-password"
                  type="password"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Leave blank to keep current password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="password-question" className="block text-sm font-medium text-gray-700">
                  Password Question (Optional)
                </label>
                <input
                  id="password-question"
                  type="text"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter password recovery question"
                  value={passwordQuestion}
                  onChange={(e) => setPasswordQuestion(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="password-answer" className="block text-sm font-medium text-gray-700">
                  Password Answer (Optional)
                </label>
                <input
                  id="password-answer"
                  type="text"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter password recovery answer"
                  value={passwordAnswer}
                  onChange={(e) => setPasswordAnswer(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="mt-6 flex gap-2">
              <button
                onClick={() => setShowUpdateModal(false)}
                disabled={isLoading}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateAccount}
                disabled={isLoading}
                className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAccount;
